// The per-user notification inbox (SEP-264 decision 17, SEP-317 section 7).
//
// One row names one staff user, one notification family and the record that
// the notification is about. PA Dealing and Transfers write to the same inbox.
// Each reader asks for the subject kind of its own records, so the PA Dealing
// panel never shows a transfer update and the Transfers panel never shows a
// PA Dealing decision.

import { z } from 'zod';

export const userNotificationKindSchema = z.enum([
    'pa_request_auto_cleared',
    'pa_request_approved',
    'pa_request_rejected',
    'pa_information_requested',
    'pa_request_expired',
    'pa_clearance_extended',
    'pa_request_withdrawn_by_compliance',
    /** Compliance wrote a comment that the employee can read (review U2). */
    'pa_compliance_comment',
    /** Operations added a note that the Desk can read (SEP-317 section 7). */
    'transfer_desk_note',
    /** Operations needs an action of the Desk on one holding (SEP-317 section 4.4). */
    'transfer_holding_action_required',
    /** Operations changed one holding, visible to the Desk (SEP-317 section 4.5). */
    'transfer_holding_update',
    /** Operations completed a challenged transfer again (SEP-317 section 10.3). */
    'transfer_challenge_resolved',
]);
export type UserNotificationKind = z.infer<typeof userNotificationKindSchema>;

export const userNotificationSubjectKindSchema = z.enum(['pa_trade_request', 'transfer_request']);
export type UserNotificationSubjectKind = z.infer<typeof userNotificationSubjectKindSchema>;

export const userNotificationSchema = z.object({
    id: z.string(),
    kind: userNotificationKindSchema,
    title: z.string(),
    body: z.string().nullable(),
    subjectKind: userNotificationSubjectKindSchema,
    subjectId: z.string(),
    /** A record inside the subject, for example one holding line of a transfer. Null when there is none. */
    relatedId: z.string().nullable(),
    /** The path that opens the record under the caller's active view. */
    route: z.string(),
    createdAt: z.string(),
    readAt: z.string().nullable(),
});
export type UserNotification = z.infer<typeof userNotificationSchema>;

export const userNotificationsResponseSchema = z.object({
    notifications: z.array(userNotificationSchema),
    unreadCount: z.number().int().nonnegative(),
});
export type UserNotificationsResponse = z.infer<typeof userNotificationsResponseSchema>;
